import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "./Icon";

/**
 * Dialog — composes `@norkron/web-dialog` + `@norkron/web-portal` +
 * `@norkron/web-dismiss`. Renders through a portal, closes on Escape / scrim
 * click, and restores nothing the caller didn't own. Focus lands on the first
 * focusable child (native autofocus).
 */

export function Dialog({
  title,
  subtitle,
  onClose,
  children,
  testid,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  testid?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testid}
      >
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <h2 className="dialog__title">{title}</h2>
            {subtitle ? <p className="dialog__sub">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            className="btn btn--ghost"
            aria-label="Close"
            onClick={onClose}
            style={{ padding: 6 }}
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
